import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useColorMode } from '@chakra-ui/react';
import { EditFill } from '@peacecover/icon';
import { useGetBettieChatRoomPictures } from '../../domains/bettie/ui/hooks/useGetBettieChatRoomPictures';
import { useGetBettieRecommendPictures } from '../../domains/bettie/ui/hooks/useGetBettieRecommendPictures';
import { useGetChatRoomDetail } from '../../domains/chat/ui/hooks/useGetChatRoomDetail';
import { useApplyBettieProfilePicture } from './hooks/useApplyBettieProfilePicture';
import { useBettieRecommendedProfilePictures } from './hooks/useBettieRecommendedProfilePictures';
import { useBettieProfileImageOverlay } from './hooks/useBettieProfileImageOverlay';
import { useLocalBettieBackupPictures } from './hooks/useLocalBettieBackupPictures';
import { useSuccessPhotoCardToast } from '../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import BettieNicknameUpdateBottomSheet from '../../components/bottomSheet/bettie/BettieNicknameUpdateBottomSheet';
import BettieProfileImageOverlay from './components/BettieProfileImageOverlay';
import HorizontalProfileSection from './components/HorizontalProfileSection';
import BettieCenterProfileImage from './components/BettieCenterProfileImage';
import BettieChangeProfileHeader from './components/BettieChangeProfileHeader';
import { ReactComponent as GradientAttention } from '../../icons/gradient.attention.svg';
import type { components } from '../../types/schema';

type ChatRoomUserPicture = components['schemas']['ApiV1BettieChatRoomUserPicture'] & {
  nickname?: string;
};

const BettieChangeProfilePage = () => {
  // ---------- query / route ----------
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const { chatRoomId } = useParams<{ chatRoomId: string }>();
  const { data: chatRoom } = useGetChatRoomDetail(chatRoomId ?? '');
  const { data: bettieChatRoomPictures } = useGetBettieChatRoomPictures(chatRoomId ?? '');
  const {
    data: bettieRecommendPictures,
    persistNextPageToLoad,
    fetchNextPage: fetchNextRecommendPicturesPage,
    hasNextPage: hasNextRecommendPicturesPage,
    isFetchingNextPage: isFetchingNextRecommendPicturesPage
  } = useGetBettieRecommendPictures(chatRoomId ?? '');
  const showSuccessToast = useSuccessPhotoCardToast();

  // ---------- local ui state ----------
  const [isProfileUpdateSheetOpen, setIsProfileUpdateSheetOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedProfilePicture, setSelectedProfilePicture] = useState<ChatRoomUserPicture | null>(
    null
  );

  // 추천 성공/프사 추가 완료 시, 하단 가로 스크롤을 항상 왼쪽으로 복원하기 위한 reset key
  const [horizontalScrollResetKey, bumpHorizontalScrollResetKey] = useReducer(
    (prev: number) => prev + 1,
    0
  );

  // ---------- local pictures (uploads / backups) ----------
  const {
    localBackupPictures,
    localOriginalDataUrlByPictureUrl,
    addLocalUpload,
    addBackupByUrl,
    removeBackupByUrl,
    clearLocalUploadMemory
  } = useLocalBettieBackupPictures();

  // ---------- derived ----------
  const mergedProfilePictures = useMemo(() => {
    return [...localBackupPictures, ...(bettieChatRoomPictures ?? [])].filter((picture) =>
      Boolean(picture.pictureUrl)
    );
  }, [bettieChatRoomPictures, localBackupPictures]);

  const existingPictureUrls = useMemo(
    () => mergedProfilePictures.map((p) => p.pictureUrl),
    [mergedProfilePictures]
  );

  const flattenedRecommendPictures = useMemo(() => {
    return bettieRecommendPictures?.pages?.flatMap((page) => page.userMemberPictures) ?? [];
  }, [bettieRecommendPictures]);

  const lastRecommendPagination = useMemo(() => {
    const pages = bettieRecommendPictures?.pages;
    const last = pages?.[pages.length - 1];
    return last?.meta?.pagination;
  }, [bettieRecommendPictures]);

  const lastRecommendFetchedPage = lastRecommendPagination?.currentPage;
  const lastRecommendTotalPages = lastRecommendPagination?.totalPages;

  // 나갈 때: 다음번에 불러올 페이지(nextPage)를 sessionStorage에 저장
  useEffect(() => {
    return () => {
      const currentPage =
        typeof lastRecommendFetchedPage === 'number' ? lastRecommendFetchedPage : 0;
      const totalPages = typeof lastRecommendTotalPages === 'number' ? lastRecommendTotalPages : 0;

      // totalPages를 모르면(아직 fetch 전) 1페이지로 시작
      if (currentPage < 1 || totalPages < 1) {
        persistNextPageToLoad(1);
        return;
      }

      const nextPage = currentPage >= totalPages ? 1 : currentPage + 1;
      persistNextPageToLoad(nextPage);
    };
  }, [lastRecommendFetchedPage, lastRecommendTotalPages, persistNextPageToLoad]);

  // ---------- recommend flow ----------
  const {
    handleBeforeRecommendNewProfile,
    handleAfterRecommendNewProfile,
    usedRecommendPictureUrls,
    previewRecommendPictureUrl,
    remainingCount
  } = useBettieRecommendedProfilePictures({
    recommendPictures: flattenedRecommendPictures,
    existingPictureUrls,
    onAppendRecommendedPictureUrl: (pictureUrl, userNickname) => {
      // 추천 프사는 선택 시 Center에서 닉네임이 보이도록 로컬 backup에 nickname을 함께 저장
      // 최신 추천이 HorizontalProfileSection 맨 앞으로 오도록 front에 삽입
      addBackupByUrl({ pictureUrl, position: 'front', nickname: userNickname });
      // 광고 시청 성공(=실제 추천 프사 추가) 시점에만 가로 스크롤을 왼쪽으로 복원
      bumpHorizontalScrollResetKey();
    },
    hasNextPage: hasNextRecommendPicturesPage,
    isFetchingNextPage: isFetchingNextRecommendPicturesPage,
    onFetchNextPage: () => {
      fetchNextRecommendPicturesPage();
    },
    onFailedRecommend: () => {
      return showSuccessToast(
        'bottom',
        t('BettieChangeProfile.noMoreRecommendPicture') as string,
        false,
        <GradientAttention width={20} height={20} />
      );
    }
  });

  // 남은 추천 프사가 0이면 다음 페이지를 미리 불러와서 다음 클릭에서 바로 추천 가능하도록 함
  const prefetchTriggeredForPageRef = useRef<number | null>(null);
  useEffect(() => {
    if (remainingCount > 0) return;
    if (!hasNextRecommendPicturesPage) return;
    if (isFetchingNextRecommendPicturesPage) return;

    const pageKey = typeof lastRecommendFetchedPage === 'number' ? lastRecommendFetchedPage : 0;
    if (prefetchTriggeredForPageRef.current === pageKey) return;
    prefetchTriggeredForPageRef.current = pageKey;

    fetchNextRecommendPicturesPage();
  }, [
    fetchNextRecommendPicturesPage,
    hasNextRecommendPicturesPage,
    isFetchingNextRecommendPicturesPage,
    lastRecommendFetchedPage,
    remainingCount
  ]);

  // ---------- render data ----------
  const backupProfilePictures = useMemo(() => {
    return mergedProfilePictures.filter(
      (picture) => picture.pictureUrl !== selectedProfilePicture?.pictureUrl
    );
  }, [mergedProfilePictures, selectedProfilePicture?.pictureUrl]);

  const isSelectedCurrentProfile = selectedProfilePicture?.type === 'current';

  // ---------- effects ----------
  useEffect(() => {
    const currentProfilePicture = bettieChatRoomPictures?.find(
      (picture) => picture.type === 'current'
    );

    if (currentProfilePicture) {
      setSelectedProfilePicture(currentProfilePicture);
    }
  }, [bettieChatRoomPictures]);

  useEffect(() => {
    if (!isSelectedCurrentProfile) setIsMenuOpen(false);
  }, [isSelectedCurrentProfile]);

  // ---------- apply flow ----------
  const {
    handleApplySelectedProfilePicture,
    handleApplyLocalUploadAsCurrentProfile,
    isApplyLoading
  } = useApplyBettieProfilePicture({
    chatRoomId,
    selectedProfilePicture,
    localOriginalDataUrlByPictureUrl,
    usedRecommendPictureUrls,
    onClearLocalUploadMemory: clearLocalUploadMemory,
    onRemoveLocalBackupPictureByUrl: removeBackupByUrl
  });

  const handleOverlayConfirmApplyNow = useCallback(
    async ({ dataUrl, originalDataUrl }: { dataUrl: string; originalDataUrl: string }) => {
      const nextPicture: ChatRoomUserPicture = {
        type: 'backup',
        pictureUrl: dataUrl,
        thumbnailUrl: null,
        originalPictureUrl: null
      };
      setSelectedProfilePicture(nextPicture);
      addLocalUpload({ dataUrl, originalDataUrl });
      bumpHorizontalScrollResetKey();

      await handleApplyLocalUploadAsCurrentProfile({ dataUrl, originalDataUrl });
    },
    [addLocalUpload, handleApplyLocalUploadAsCurrentProfile]
  );

  const handleOverlayConfirmBackupOnly = useCallback(
    ({ dataUrl, originalDataUrl }: { dataUrl: string; originalDataUrl: string }) => {
      addLocalUpload({ dataUrl, originalDataUrl });
      bumpHorizontalScrollResetKey();
    },
    [addLocalUpload]
  );

  const overlayConfirmHandlerRef = useRef<
    ((payload: { dataUrl: string; originalDataUrl: string }) => void) | null
  >(null);

  const { openImagePicker, fileInputProps, overlayProps } = useBettieProfileImageOverlay({
    onConfirm: (payload) => overlayConfirmHandlerRef.current?.(payload)
  });

  const openImagePickerForApplyNow = useCallback(() => {
    overlayConfirmHandlerRef.current = handleOverlayConfirmApplyNow;
    openImagePicker();
  }, [handleOverlayConfirmApplyNow, openImagePicker]);

  const openImagePickerForBackupOnly = useCallback(() => {
    overlayConfirmHandlerRef.current = handleOverlayConfirmBackupOnly;
    openImagePicker();
  }, [handleOverlayConfirmBackupOnly, openImagePicker]);

  const hasNoBettiePictures =
    bettieChatRoomPictures?.length === 0 ||
    bettieChatRoomPictures?.every((picture) => !picture.pictureUrl);

  if (!chatRoom) return null;

  return (
    <div
      className="min-h-screen pt-[env(safe-area-inset-top)] overflow-hidden"
      style={{
        backgroundImage: `url(/assets/bettie/bg_bettie_change_profile_${colorMode}.png)`,
        backgroundRepeat: 'round'
      }}
    >
      <BettieChangeProfileHeader
        bettieNickname={chatRoom?.bettieNickname ?? ''}
        setIsMenuOpen={setIsMenuOpen}
        isMenuOpen={isMenuOpen}
        currentProfilePicture={selectedProfilePicture ?? null}
        chatRoomId={chatRoomId ?? ''}
      />
      <div className="flex flex-col items-center">
        <BettieNicknameSection
          bettieNickname={chatRoom?.bettieNickname ?? ''}
          onEditClick={() => setIsProfileUpdateSheetOpen(true)}
        />
        <BettieCenterProfileImage
          currentProfilePicture={selectedProfilePicture ?? null}
          onApplyProfilePicture={handleApplySelectedProfilePicture}
          onClickAddProfileImage={openImagePickerForApplyNow}
          isApplyLoading={isApplyLoading}
        />
        {!hasNoBettiePictures && (
          <div className="mt-[27px] w-full">
            <HorizontalProfileSection
              bettieChatRoomPictures={backupProfilePictures ?? []}
              hasRecommendPictures={
                flattenedRecommendPictures.length > 3 || !!hasNextRecommendPicturesPage
              }
              recommendPreviewPictureUrl={previewRecommendPictureUrl}
              onClickAddProfileImage={openImagePickerForBackupOnly}
              onSwitchProfilePicture={(picture) => setSelectedProfilePicture(picture)}
              onBeforeRecommendNewProfile={handleBeforeRecommendNewProfile}
              onAfterRecommendNewProfile={handleAfterRecommendNewProfile}
              horizontalScrollResetKey={horizontalScrollResetKey}
            />
          </div>
        )}
        <Footer />
      </div>
      <input {...fileInputProps} />
      <BettieProfileImageOverlay {...overlayProps} />
      {isProfileUpdateSheetOpen && (
        <BettieNicknameUpdateBottomSheet
          isOpen={isProfileUpdateSheetOpen}
          onClose={() => setIsProfileUpdateSheetOpen(false)}
          bettieProfile={chatRoom?.bettieProfile}
          chatRoomId={chatRoomId ?? ''}
        />
      )}
    </div>
  );
};

export default BettieChangeProfilePage;

const BettieNicknameSection = ({
  bettieNickname,
  onEditClick
}: {
  bettieNickname: string;
  onEditClick: () => void;
}) => {
  return (
    <div className="flex items-center gap-[6px] mt-[74px]">
      <p className="text-[26px] font-bold leading-[31px] text-text-main-lv1">{bettieNickname}</p>
      <button
        type="button"
        className="p-[6px] rounded-full bg-secondary-light dark:bg-secondary-dark"
        onClick={onEditClick}
      >
        <EditFill size={16} className="text-primary-light dark:text-primary-dark" />
      </button>
    </div>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="fixed bottom-0 left-[14px] pb-[calc(env(safe-area-inset-bottom)+10px)]">
      <ul className="text-[13px] leading-[21px] text-text-sub-lv1 list-disc list-inside text-nowrap">
        <li>{t('BettieChangeProfile.visibilityDescription1')}</li>
        <li>{t('BettieChangeProfile.visibilityDescription2')}</li>
        <li>{t('BettieChangeProfile.visibilityDescription3')}</li>
      </ul>
    </footer>
  );
};
